import { formatBusinessDate, formatPaise } from "@luckymenu/core";
import { useTranslation } from "@luckymenu/i18n/react";
import { useMySubscription } from "@luckymenu/staff-core";
import { Button } from "@luckymenu/ui-web";
import { Download } from "lucide-react";

/**
 * The owner's exception on the paused screen (auth-tenancy.md, Suspension):
 * what is due, how to pay, and the invoices to download. Other roles see
 * only the paused message.
 */
export function OwnerBilling() {
	const { t } = useTranslation("staff");
	const billing = useMySubscription();
	if (billing.status === "loading") return null;
	if (billing.status === "error") {
		return (
			<div className="flex flex-col gap-2" role="alert">
				<p className="text-sm text-destructive">{t("suspended.loadFailed")}</p>
				<Button variant="outline" onClick={billing.retry}>
					{t("suspended.retry")}
				</Button>
			</div>
		);
	}
	const upiId = billing.payTo?.upiId;
	return (
		<section aria-labelledby="owner-billing" className="flex flex-col gap-3">
			<h2 id="owner-billing" className="text-base font-semibold">
				{t("suspended.ownerTitle")}
			</h2>
			{billing.due.length === 0 ? (
				<p className="text-sm text-muted-foreground">{t("suspended.nothingDue")}</p>
			) : (
				<>
					<ul className="flex flex-col divide-y rounded-md border">
						{billing.due.map((invoice) => (
							<li key={invoice.id} className="flex items-center justify-between gap-3 p-3">
								<div className="min-w-0">
									<p className="text-sm font-medium">
										{t("suspended.invoice", { invoiceNo: invoice.invoiceNo })}
									</p>
									<p className="text-sm text-muted-foreground">
										{t("suspended.owed", {
											amount: formatPaise(invoice.owedPaise),
											date: formatBusinessDate(invoice.dueDate),
										})}
									</p>
								</div>
								<Button
									variant="outline"
									size="sm"
									disabled={billing.downloading !== null}
									onClick={() =>
										void billing.download(invoice.id).then((link) => {
											// An attachment: the browser saves it and stays on this page.
											if (link) window.location.assign(link.url);
										})
									}
								>
									<Download data-icon="inline-start" />
									{billing.downloading === invoice.id
										? t("suspended.downloading")
										: t("suspended.download")}
								</Button>
							</li>
						))}
					</ul>
					<p className="text-sm">
						{upiId ? t("suspended.payTo", { upiId }) : t("suspended.payToNoUpi")}
					</p>
					<p className="text-sm text-muted-foreground">{t("suspended.ownerBody")}</p>
				</>
			)}
			{billing.failed ? (
				<p className="text-sm text-destructive" role="alert">
					{t("suspended.downloadFailed")}
				</p>
			) : null}
		</section>
	);
}
